import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories, gossipItems } from "@/mocks/gossip";

const navItems = [
  { label: "首页", to: "/" },
  ...categories.map((cat) => ({ label: cat.name, to: `/category/${cat.slug}` })),
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return [];
    return gossipItems
      .filter((item) => item.title.includes(kw) || item.tags.some((tag) => tag.includes(kw)))
      .slice(0, 6);
  }, [keyword]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname === to;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full items-center gap-6 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer flex-shrink-0">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-primary-500 text-background-50">
            <i className="ri-live-fill w-5 h-5 flex items-center justify-center"></i>
          </span>
          <span className="leading-none">
            <span className="block font-heading text-lg font-bold text-foreground-950">瓜田影像社</span>
            <span className="block text-[10px] tracking-widest text-foreground-500 mt-0.5">视频吃瓜 · 第一现场</span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden lg:flex flex-1 items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-700 font-medium"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="搜索站内视频"
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-background-300 text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-search-line w-5 h-5 flex items-center justify-center"></i>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md border border-background-300 text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} w-5 h-5 flex items-center justify-center`}></i>
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <nav aria-label="移动端导航" className="mx-auto grid max-w-7xl grid-cols-2 gap-2 px-4 py-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`cursor-pointer rounded-md px-3 py-2.5 text-sm ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-700 font-medium"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      ) : null}

      {searchOpen ? (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground-950/50 px-4 pt-24 animate-fade-in">
          <div className="w-full max-w-xl rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center gap-2 rounded-md border border-background-300 px-3 py-2">
              <i className="ri-search-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索现场视频、标签，如「路透」「安可」"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="关闭搜索"
                className="cursor-pointer text-foreground-500 transition-colors hover:text-primary-600"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            {keyword.trim() ? (
              results.length > 0 ? (
                <ul className="mt-3 flex flex-col gap-1">
                  {results.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/video/${item.id}`}
                        className="flex items-center gap-3 rounded-md p-2 cursor-pointer transition-colors hover:bg-background-100"
                      >
                        <span className="w-16 h-10 flex-shrink-0 overflow-hidden rounded-md bg-secondary-200">
                          <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-foreground-900">{item.title}</span>
                          <span className="block text-xs text-foreground-500">{item.tags.join(" · ")}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-sm text-foreground-500">没有找到相关视频，换个关键词试试。</p>
              )
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                {["路透", "第一现场", "完整版", "安可", "彩排", "分直拍"].map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setKeyword(tag)}
                    className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </header>
  );
}